import { useEffect, useRef, useState } from "react";
import { ChevronDown, PenLine } from "lucide-react";

/**
 * Dropdown of in-stock options with an optional "type your own" entry.
 * options: [{ value: string, label: string, hint?: string }]
 */
export const StockSelect = ({ value = "", options, onChange, placeholder = "", testId, allowCustom = true, inputType = "text", disabled = false, resetKey }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const wrapRef = useRef(null);
  const inStock = options.some((o) => o.value === String(value));

  useEffect(() => {
    const onClickOut = (e) => wrapRef.current && !wrapRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClickOut);
    return () => document.removeEventListener("mousedown", onClickOut);
  }, []);

  // Parent selection changed (e.g. new grade) → go back to the list.
  useEffect(() => { setCustom(false); }, [resetKey]);
  // Typed-in values that aren't in stock stay in custom mode.
  useEffect(() => { if (value && !inStock && allowCustom) setCustom(true); }, [value, inStock, allowCustom]);
  // Value matches a stock option (e.g. options loaded after mount) → show it in the list.
  useEffect(() => { if (inStock) setCustom(false); }, [inStock]);

  if (custom || (!options.length && allowCustom && !disabled)) {
    return (
      <div className="relative" data-testid={testId}>
        <input
          data-testid={`${testId}-custom-input`}
          type={inputType} step="any" autoFocus={custom && !value}
          value={value} placeholder={placeholder} disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 pl-2 pr-16 border border-amber-400 bg-amber-50/40 rounded-sm text-sm font-mono uppercase disabled:opacity-50"
        />
        {options.length > 0 && (
          <button type="button" data-testid={`${testId}-back-to-list`} onClick={() => { setCustom(false); onChange(""); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900 px-1.5 py-1">
            List
          </button>
        )}
      </div>
    );
  }

  const selected = options.find((o) => o.value === String(value));
  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button" data-testid={`${testId}-trigger`} disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full h-10 pl-2 pr-8 border border-slate-300 rounded-sm text-sm font-mono text-left bg-white hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {selected ? selected.label : <span className="text-slate-400">{placeholder}</span>}
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
      </button>
      {open && (
        <div data-testid={`${testId}-dropdown`} className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-64 overflow-auto">
          {options.map((o) => (
            <button key={o.value} type="button" data-testid={`${testId}-opt-${o.value}`} disabled={o.disabled}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${o.disabled ? "" : "hover:bg-slate-100"} ${o.value === String(value) ? "bg-slate-50" : ""}`}>
              <span className="font-mono text-slate-900">{o.label}</span>
              {o.hint && <span className="text-[10px] text-slate-500 whitespace-nowrap">{o.hint}</span>}
            </button>
          ))}
          {allowCustom && (
            <button type="button" data-testid={`${testId}-opt-custom`}
              onClick={() => { setCustom(true); onChange(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm border-t border-slate-100 text-blue-700 hover:bg-blue-50 flex items-center gap-2">
              <PenLine className="h-3.5 w-3.5" /> Type my own…
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default StockSelect;
